import React, { useEffect, useState, useCallback } from 'react';
import Button from '../../../components/common/Button';
import GradientButton from '../../../components/common/GradientButton';
import Icon from '../../../components/common/Icon';
import Input from '../../../components/common/Input';
import Pagination from '../../../components/common/Pagination';
import Modal from '../../../components/common/Modal';
import Card from '../../../components/common/Card';
import StatCard from '../../../components/common/StatCard';
import StatusBadge from '../../../components/common/StatusBadge';
import JalaliDatePicker from '../../../components/common/JalaliDatePicker';
import { getTransactionsApi, createTransactionApi, getFinancialSummaryApi, uploadReceiptApi } from '../../../api/financial.api';
import { getClubsApi } from '../../../api/club.api';
import { TRANS_TYPE_LABELS, TRANS_STATUS_LABELS, TRANS_STATUS_COLORS, formatCurrency } from '../../../utils/constants';
import { toJalali } from '../../../utils/jalali';

const emptyForm = {
  clubId: '',
  type: 'INCOME',
  category: '',
  amount: '',
  description: '',
  date: '',
};

export default function FinancialManagement() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [filters, setFilters] = useState({ clubId: '', type: '', status: '', from: '', to: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [receiptFile, setReceiptFile] = useState(null);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState('');
  const [loading, setLoading] = useState(false);

  const loadClubs = useCallback(async () => {
    try {
      const { data } = await getClubsApi({ limit: 100 });
      setClubs(data.data);
    } catch {}
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const clean = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      const [txRes, sumRes] = await Promise.all([
        getTransactionsApi({ ...clean, page: pagination.page, limit: pagination.limit }),
        getFinancialSummaryApi(clean),
      ]);
      setTransactions(txRes.data.data);
      setPagination(prev => ({ ...prev, total: txRes.data.meta.total, totalPages: txRes.data.meta.totalPages }));
      setSummary(sumRes.data.data);
    } catch (err) {
      setSnackbar(err.response?.data?.message || 'خطا در دریافت اطلاعات مالی');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => { loadClubs(); }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await createTransactionApi({
        ...form,
        clubId: form.clubId || null,
        amount: Number(form.amount),
        date: form.date || null,
      });
      if (receiptFile) await uploadReceiptApi(res.data.id, receiptFile);
      setSnackbar('تراکنش با موفقیت ثبت شد');
      setFormOpen(false);
      setForm(emptyForm);
      setReceiptFile(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      CONFIRMED: 'active',
      PENDING: 'pending',
      REJECTED: 'suspended',
    };
    return <StatusBadge status={map[status] || 'inactive'} label={TRANS_STATUS_LABELS[status]} />;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-text-dark">حسابداری</h1>
          <p className="text-sm text-slate-400">مدیریت تراکنش‌ های مالی، درآمد و هزینه</p>
        </div>
        <GradientButton onClick={() => setFormOpen(true)}>
          <Icon name="plus" className="ml-2 h-4 w-4" />
          تراکنش جدید
        </GradientButton>
      </div>

      {summary && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <StatCard
            title="مجموع درآمد"
            value={formatCurrency(summary.totalIncome)}
            color="green"
            icon="wallet"
          />
          <StatCard
            title="مجموع هزینه"
            value={formatCurrency(summary.totalExpense)}
            color="red"
            icon="wallet"
          />
          <StatCard
            title="تراز مالی"
            value={formatCurrency(summary.balance)}
            color={summary.balance >= 0 ? 'green' : 'red'}
            icon="trendingUp"
          />
        </div>
      )}

      <Card>
        <div className="flex flex-wrap gap-3 mb-4">
          <select
            className="rounded-xl border-2 border-slate-200 p-2 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
            value={filters.clubId}
            onChange={(e) => setFilters({ ...filters, clubId: e.target.value })}
          >
            <option value="">همه باشگاه‌ها</option>
            {clubs.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            className="rounded-xl border-2 border-slate-200 p-2 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          >
            <option value="">همه انواع</option>
            {Object.entries(TRANS_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select
            className="rounded-xl border-2 border-slate-200 p-2 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">همه وضعیت‌ ها</option>
            {Object.entries(TRANS_STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <Input
            placeholder="از تاریخ (مثلا 1403/01/01)"
            value={filters.from}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            className="h-11 w-40"
          />
          <Input
            placeholder="تا تاریخ"
            value={filters.to}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            className="h-11 w-40"
          />
        </div>

        {snackbar && (
          <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700 animate-fade-in">
            {snackbar}
            <button className="mr-2" onClick={() => setSnackbar('')}>✕</button>
          </div>
        )}

        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-right text-sm">
                <thead className="bg-gradient-to-r from-emerald-50 to-white border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">تاریخ</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">نوع</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">دسته</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">باشگاه</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">مبلغ</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">وضعیت</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {transactions.map(t => (
                    <tr key={t.id} className="transition-colors duration-200 hover:bg-emerald-50/50">
                      <td className="px-4 py-3">{toJalali(t.date)}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                          t.type === 'INCOME' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {TRANS_TYPE_LABELS[t.type]}
                        </span>
                      </td>
                      <td className="px-4 py-3">{t.category}</td>
                      <td className="px-4 py-3">{t.club?.name || '-'}</td>
                      <td className="px-4 py-3 text-left font-mono font-bold">
                        {formatCurrency(t.amount)}
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(t.status)}</td>
                    </tr>
                  ))}
                  {transactions.length === 0 && (
                    <tr>
                      <td colSpan="6" className="py-8 text-center text-slate-400">
                        تراکنشی ثبت نشده است
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </Card>

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title="ثبت تراکنش جدید" size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <select
              className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              {Object.entries(TRANS_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <Input
              label="دسته‌بندی"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              required
            />
            <Input
              label="مبلغ (تومان)"
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              required
            />
            <select
              className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
              value={form.clubId}
              onChange={(e) => setForm({ ...form, clubId: e.target.value })}
            >
              <option value="">بدون باشگاه</option>
              {clubs.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <JalaliDatePicker
              label="تاریخ تراکنش"
              value={form.date}
              onChange={(date) => setForm({ ...form, date: date })}
            />
            <Input
              label="توضیحات"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="col-span-2"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" type="button" onClick={() => setFormOpen(false)}>انصراف</Button>
            <GradientButton type="submit">ثبت تراکنش</GradientButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}